import './CtaCampaign.scss'

import {Background} from '@codeurs/front'
import React, {useState} from 'react'
import {ImageType, LinkType, WysiwygType} from '../../../types'
import {style} from '../../../util/style'
import {Theme} from '../../Theme'
import {dateFromServerDate} from '../../../util/dates'
import isWithinInterval from 'date-fns/isWithinInterval'
import getDate from 'date-fns/getDate'
import {isAfter, isBefore} from 'date-fns'

const styles = style('ctacampaign', ['image'])

export type CtaCampaignType = CtaCampaignImageType & {
	title1?: string
	title2?: string
	text?: WysiwygType
	button?: LinkType
	publication_period_start: string
	publication_period_end: string
}

type CtaCampaignImageType = {
	image?: ImageType
}

export const CtaCampaign: React.FC<CtaCampaignType> = ({
	title1,
	title2,
	text,
	button,
	image,
	publication_period_start: publicationStart,
	publication_period_end: publicationEnd
}) => {
	const range: RangeType = {
		start: publicationStart,
		end: publicationEnd
	}
	const publishType = getPublishType(range)
	const isPublished = getPublishedByType(publishType, range)

	if (!isPublished) return null
	if (!title1 && !text && !button) return null
	return (
		<div className={styles()}>
			<div className={styles('row')}>
				<div className={styles('content')}>
					<div className={styles('content-titles')}>
						{title1 && (
							<Theme.H3
								className={styles('content-titles-title1')}
								mod={['green', 'marked', 'slantleft']}
							>
								{title1}
							</Theme.H3>
						)}
						{title2 && (
							<Theme.H3
								className={styles('content-titles-title2')}
								mod={['marked', 'slantright']}
							>
								{title2}
							</Theme.H3>
						)}
					</div>
					<div className={styles('content-bottom')}>
						{text && (
							<div className={styles('content-bottom-text')}>
								<Theme.Wysiwyg>{text}</Theme.Wysiwyg>
							</div>
						)}
						{button && button.url && (
							<div className={styles('content-bottom-button')}>
								<Theme.Button to={button.url} mod="outline">
									{button.description || button.title}
								</Theme.Button>
							</div>
						)}
					</div>
				</div>
				<CtaCampaignImage image={image} />
			</div>
		</div>
	)
}

const CtaCampaignImage: React.FC<CtaCampaignImageType> = ({image}) => {
	if (!image || !image.src) return null
	const [isLoaded, setLoaded] = useState(false)

	return (
		<div className={styles.image().is({loaded: isLoaded})}>
			<Background
				img={image}
				className={styles.image('bg').toString()}
				onload={() => setLoaded(true)}
			/>
		</div>
	)
}

type PublishedType = 'from' | 'until' | 'between'

type RangeType = {
	start: string
	end: string
}

const getPublishType = ({start, end}: RangeType): PublishedType => {
	if (start && !end) return 'from'
	if (!start && end) return 'until'
	if (start && end) return 'between'
}

const getPublishedByType = (type: PublishedType, range: RangeType): boolean => {
	const {start = null, end = null} = range
	const startServerDate = dateFromServerDate(start)
	const endServerDate = dateFromServerDate(end)

	const todayDate = new Date()

	switch (type) {
		case 'between':
			const todayIsInInterval = isWithinInterval(todayDate, {
				start: startServerDate,
				end: endServerDate
			})
			return todayIsInInterval
		case 'until':
			const todayIsBeforeEnd = isBefore(todayDate, endServerDate)
			return todayIsBeforeEnd
		case 'from':
			const todayIsAfterStart = isAfter(todayDate, startServerDate)
			return todayIsAfterStart
		default:
			return true
	}
}
